import * as React from 'react';

import { CircleX, LucideProps, LucideSearch } from 'lucide-react';
import {
  SearchField as AriaSearchField,
  SearchFieldProps as AriaSearchFieldProps,
  ValidationResult as AriaValidationResult,
  composeRenderProps,
  Input,
  Text,
} from 'react-aria-components';

import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';
import { FieldError, FieldGroup, Label } from '../Field';
import { VirtualKeyboardWrapper } from '../keyboard/VirtualKeyboardWrapper';

/**
 * SearchField props interface.
 * Extends AriaSearchFieldProps and adds styling variants.
 */
interface SearchFieldProps extends AriaSearchFieldProps {
  /** Label text displayed above the input */
  label?: string;
  /** Description text displayed below the label */
  description?: string;
  /** Error message shown when validation fails. Can be a string or a function that returns a string based on validation results */
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  /** Placeholder to be set in the input. */
  placeholder?: string;
  /** Whether to show the search icon */
  hideIcon?: boolean;
  /** Whether to show the clear button */
  showClearButton?: boolean;
  /** Custom icon to be shown to the lefthand side of the input. */
  icon?: React.ReactElement<LucideProps & { className?: string }>;
  /** Whether to use dark theme styling */
  dark?: boolean;
  /** Content to be rendered to the right of the input */
  suffix?: React.ReactNode;
  /** Size flag to render different size of input and font */
  size?: 'default' | 'lg' | 'xl';
  touchModeEnabled?: boolean;
  disableVirtualKeyboard?: boolean;
}

type SearchFieldInnerProps = Omit<
  SearchFieldProps,
  'touchModeEnabled' | 'disableVirtualKeyboard'
> & {
  shouldUseVirtualKeyboard: boolean;
};

const SearchFieldInner = ({
  className,
  label,
  description,
  errorMessage,
  placeholder = 'Search...',
  icon,
  hideIcon,
  showClearButton,
  dark,
  suffix,
  size = 'default',
  shouldUseVirtualKeyboard,
  ...props
}: SearchFieldInnerProps) => (
  <AriaSearchField
    className={composeRenderProps(className, (className) =>
      cn(
        '@SearchFieldInner',
        '@SearchField group flex flex-col gap-2',
        size === 'default' && 'h-input',
        className,
      ),
    )}
    aria-label={label ?? 'Search'}
    {...props}
  >
    {label && <Label>{label}</Label>}
    <FieldGroup
      dark={dark}
      className={cn(
        'flex items-center gap-2',
        size === 'xl' && 'h-13 border-2 p-3',
      )}
      suffix={suffix}
    >
      {!hideIcon &&
        ((icon &&
          React.cloneElement(icon, {
            size: 14,
            className: 'text-current opacity-50 shrink-0',
          })) ?? (
          <LucideSearch
            size={14}
            className="shrink-0 text-current opacity-50"
          />
        ))}
      <Input
        className={cn(
          'w-full flex-1 bg-transparent outline-none placeholder:text-foreground/20',
          size === 'xl' && 'h-13',
        )}
        placeholder={placeholder}
        {...(shouldUseVirtualKeyboard ? { inputMode: 'none' as const } : {})}
      />
      {showClearButton && (
        <Button
          slot="clear-button"
          className="size-6"
          size="icon"
          variant="text"
          color="neutral"
          icon={<CircleX />}
        />
      )}
    </FieldGroup>
    {description && (
      <Text className="text-sm text-neutral/20" slot="description">
        {description}
      </Text>
    )}
    {errorMessage && <FieldError>{errorMessage}</FieldError>}
  </AriaSearchField>
);

const SearchField = ({
  touchModeEnabled = false,
  disableVirtualKeyboard,
  ...props
}: SearchFieldProps) => {
  const shouldUseVirtualKeyboard = touchModeEnabled && !disableVirtualKeyboard;

  if (shouldUseVirtualKeyboard) {
    return (
      <VirtualKeyboardWrapper
        value={props.value ?? ''}
        onChange={props.onChange}
      >
        <SearchFieldInner shouldUseVirtualKeyboard {...props} />
      </VirtualKeyboardWrapper>
    );
  }

  return (
    <SearchFieldInner
      shouldUseVirtualKeyboard={false}
      {...props}
      className="@SearchField"
    />
  );
};

export { SearchField };
export type { SearchFieldProps };
